import { useLayoutEffect, useRef, type DependencyList } from 'react';

/**
 * The summary panel's header and footer are absolutely positioned over a plans list that fills the
 * panel, so the list can only clear them by padding itself with their measured heights. The loading
 * skeleton runs this too, which is what lets its first plan row land where the real one does
 * without hard-coding either height.
 */
export function useSummaryOverlaySpacing(deps: DependencyList) {
  const panelRef = useRef<HTMLElement | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const footerRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const panel = panelRef.current;
    const header = headerRef.current;
    const footer = footerRef.current;

    if (!panel || !header || !footer) return undefined;

    const updateOverlaySpacing = () => {
      panel.style.setProperty('--checkout-summary-header-space', `${header.getBoundingClientRect().height}px`);
      panel.style.setProperty('--checkout-summary-footer-space', `${footer.getBoundingClientRect().height}px`);
    };

    updateOverlaySpacing();

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(updateOverlaySpacing)
      : null;

    resizeObserver?.observe(header);
    resizeObserver?.observe(footer);
    window.addEventListener('resize', updateOverlaySpacing);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateOverlaySpacing);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { panelRef, headerRef, footerRef };
}
